<script lang="ts">
  import axios from 'axios';
  import { onMount, untrack } from 'svelte';
  import { clic } from '../directives/actions.svelte';
  import { profilStore } from '../stores/profil.store';
  import { fabriqueFilAriane, type PropriétésFilAriane } from '../ui/filAriane';
  import FiltresBureau from '../ui/FiltresBureau.svelte';
  import FiltresMobile from '../ui/FiltresMobile.svelte';
  import Heros from '../ui/Heros.svelte';
  import IllustrationPasDeResultatDeRecherche from '../ui/IllustrationPasDeResultatDeRecherche.svelte';
  import Lien from '../ui/Lien.svelte';
  import CarteFinancement from './CarteFinancement.svelte';
  import FiltresFinancements from './FiltresFinancements.svelte';
  import type { ResumeFinancement } from './financement';
  import SqueletteCarteFinancement from './SqueletteCarteFinancement.svelte';
  import { financementsStore } from './stores/financements.store';
  import { financementsFiltre } from './stores/financementsFiltre.store';
  import { rechercheParRegion } from './stores/rechercheParRegion.store';
  import { rechercheParTypeFinancement } from './stores/rechercheParTypeFinancement.store';
  import { rechercheParTypeOrganisation } from './stores/rechercheParTypeOrganisation.store';

  type Props = {
    financementsInitiaux?: ResumeFinancement[];
  };

  type ReponseAxios = {
    id: number;
    nom: string;
    financeur: string;
    entitesElligibles: string[];
    typesDeFinancement: string[];
    perimetresGeographiques: string[];
    regions: string[];
  }[];

  const { financementsInitiaux = [] }: Props = $props();
  financementsStore.initialise(untrack(() => financementsInitiaux));

  let financements = $state<ResumeFinancement[]>([]);
  let chargement = $state(false);

  const estConnecte = profilStore.utilisateurEstConnecte();

  const reinitialiseFiltres = () => {
    rechercheParRegion.reinitialise();
    rechercheParTypeOrganisation.reinitialise();
    rechercheParTypeFinancement.reinitialise();
  };

  onMount(async () => {
    try {
      chargement = true;
      const reponse = await axios.get<ReponseAxios>('/api/financements');
      financements = reponse.data;
    } catch {
      financements = [];
    } finally {
      chargement = false;
      financementsStore.initialise(financements ?? []);
    }
  });
  const propriétésFilAriane: PropriétésFilAriane = { feuille: 'Financements cyber', fondSombre: true };
</script>

<Heros
  titre="Financements cyber"
  description="Bénéficiez d'accompagnements financés ou de subventions pour renforcer la maturité cyber de votre organisation."
  format="banniere"
  segmentsFilAriane={fabriqueFilAriane(propriétésFilAriane, !!$profilStore)}
  theme="clair"
/>

<FiltresMobile filtreActif={$financementsFiltre.filtreActif}>
  <FiltresFinancements {chargement} {estConnecte} />
</FiltresMobile>

<dsfr-container class="financements">
  <div class="contenu-section">
    <div class="entete">
      <Lien href="https://aide.messervices.cyber.gouv.fr/fr/?chat=ouvert" blank libelle="Proposer un financement" />
    </div>
    <div class="contenu">
      <FiltresBureau filtreActif={$financementsFiltre.filtreActif}>
        <FiltresFinancements {chargement} {estConnecte} />
      </FiltresBureau>
      <div class="grille-cartes">
        {#if chargement}
          <SqueletteCarteFinancement />
          <SqueletteCarteFinancement />
          <SqueletteCarteFinancement />
          <SqueletteCarteFinancement />
          <SqueletteCarteFinancement />
        {:else}
          {#each $financementsFiltre.resultat as financement (financement.id)}
            <CarteFinancement {financement} />
          {:else}
            <div class="aucun-resultat">
              <div class="illustration">
                <IllustrationPasDeResultatDeRecherche />
              </div>
              <p class="fr-h2">Désolé, aucun résultat trouvé</p>
              <dsfr-button label="Réinitialiser les filtres" use:clic={reinitialiseFiltres}></dsfr-button>
            </div>
          {/each}
        {/if}
      </div>
    </div>
  </div>
</dsfr-container>

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  dsfr-container {
    padding: 0 0 40px;
    .contenu-section {
      padding-top: 2rem;
      padding-bottom: 3rem;

      .entete {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 2rem;
      }

      .contenu {
        display: flex;
        gap: 1.5rem;
      }

      .grille-cartes {
        display: grid;
        row-gap: 24px;
        column-gap: 24px;
        flex: 1 0 0;
        grid-template-columns: 1fr;
        margin-bottom: auto;

        @include a-partir-de(sm) {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .aucun-resultat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          grid-column: 1 / -1;
          text-align: center;

          .illustration {
            height: 250px;
          }
        }
      }
    }
  }
</style>
